import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import BackdropComponent from "components/Spinner/Spinner";
import { fontStyles } from "components/Common/commonStyles";
import classes from "../DTR/Attendance.module.css";
import {
  getDtrAttendanceRange,
  getDtrAttendance,
} from "services/learnPointUserServices";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// ASTT-7319: "Roster Id"/"Oracle Id" -> "Roster ID"/"Oracle ID" -- matches
// the capitalization ClassDetails.js already established for the same
// concept ("Roster ID", "Selected Roster IDs").
const StaticColumns = [
  { id: "classRosterId", label: "Roster ID", width: 150, resizable: false },
  { id: "oracleId", label: "Oracle ID", width: 150, resizable: false },
  { id: "fullName", label: "Trainee Name", width: 150, resizable: false },
];

const generateDateRange = (start, end) => {
  const dates = [];
  let cur = new Date(start + "T00:00:00");
  const last = new Date(end + "T00:00:00");
  while (cur <= last) {
    dates.push(new Date(cur));
    cur = new Date(cur);
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};

// Format date as M/DD/YYYY
const formatDate = (d) => {
  const m = d.getMonth() + 1;
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = d.getFullYear();
  return `${m}/${dd}/${yy}`;
};

// Merging roster + attendance data
// rosterList:  [{ classRosterId, oracleId, fullName, ttfDate, productTrainingDate, nestingDate, nestingEndDate }]
// employeeDatalist: [{ employeeNumber, applyDate, paidTimeInHours }]
const buildRows = (rosterList, employeeDatalist) => {
  const attendanceMap = {};
  employeeDatalist.forEach(({ employeeNumber, applyDate, paidTimeInHours }) => {
    const dateKey = applyDate.split("T")[0]; // "YYYY-MM-DD"
    if (!attendanceMap[employeeNumber]) attendanceMap[employeeNumber] = {};
    attendanceMap[employeeNumber][dateKey] = paidTimeInHours;
  });

  return rosterList.map((rid) => ({
    ...rid,
    attendance: attendanceMap[rid.oracleId] ?? {},
  }));
};

const toDateKey = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`; // "YYYY-MM-DD"
};

// ASTT-7319: both entries were identically "#e5e4e2" -- the whole point of
// this array is to alternate the background between two different shades
// for successive weeks, but with both values the same, getWeekBg's
// alternation (WEEK_COLORS[getWeekNumber(d) % 2]) has never actually
// produced any visible difference. Second value now genuinely distinct.
const WEEK_COLORS = ["#e5e4e2", "#f4f3f2"];
const getWeekBg = (d) => WEEK_COLORS[getWeekNumber(d) % 2];

const getWeekNumber = (d) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
};

const getPhaseColor = (rosterRow, dateKey) => {
  const ttf = rosterRow.ttfDate ? parseToISO(rosterRow.ttfDate) : null;
  const product = rosterRow.productTrainingDate
    ? parseToISO(rosterRow.productTrainingDate)
    : null;
  const nesting = rosterRow.nestingDate
    ? parseToISO(rosterRow.nestingDate)
    : null;
  const nestEnd = rosterRow.nestingEndDate
    ? parseToISO(rosterRow.nestingEndDate)
    : null;

  if (ttf && product && dateKey >= ttf && dateKey < product)
    return { bg: "#fff9c4", border: "#f9a825", tooltip: "TTF Date" };

  if (product && nesting && dateKey >= product && dateKey < nesting)
    return { bg: "#bbdefb", border: "#1976d2", tooltip: "Product Date" };

  if (nesting && nestEnd && dateKey >= nesting && dateKey <= nestEnd)
    return { bg: "#c8e6c9", border: "#00B6B2", tooltip: "Nesting Date" };

  return null;
};

const parseToISO = (str) => {
  const [mm, dd, yyyy] = str.split(" ")[0].split("/");
  return `${yyyy}-${mm}-${dd}`;
};

export default function Attendance(props) {
  const rosterId = props.classRosterId ?? null;
  const [loading, setLoading] = useState(false);
  const [classRosterId, setClassRosterId] = useState("");
  const [order, setOrder] = useState("ASC");
  const [sortFlag, setSortFlag] = useState({});
  const [attendanceData, setAttendanceData] = useState([]);
  const [dynamicColumns, setDynamicColumns] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        setClassRosterId(rosterId);
        setLoading(true);

        setAttendanceData([]);
        setDynamicColumns([]);

        // ASTT-7319 follow-up: guard added -- the resets above already ran
        // unconditionally, so clearing the Class Roster Ids selection did
        // correctly clear this grid's display; but without this guard,
        // getDtrAttendanceRange still fired with an empty rosterId every
        // time, which would produce the same kind of 400 already fixed
        // elsewhere (ClassDetails.js/TrainingNotes.js) for this identical
        // pattern.
        if (!rosterId) {
          if (!cancelled) setLoading(false);
          return;
        }

        const response = await getDtrAttendanceRange(rosterId);
        if (cancelled) return;

        if (response?.statusCode === 200) {
          const rosterList = response?.data["dtrClassRosterDetails"] ?? [];

          const { evaOracleIDs, evaStartDate, evaEndDate } =
            response?.data["dtrEvaDateRance"];

          const startDate = parseToISO(evaStartDate);
          const endDate = parseToISO(evaEndDate);
          const generated = generateDateRange(startDate, endDate);

          if (!cancelled) setDynamicColumns(generated);

          const attendanceResponse = await getDtrAttendance(
            evaOracleIDs,
            startDate,
            endDate,
          );
          const employeeDatalist =
            attendanceResponse?.data?.employeeDatalist ?? [];
          const rows = buildRows(rosterList, employeeDatalist);
          if (!cancelled) setAttendanceData(rows);

          if (cancelled) return;
        }
      } catch (error) {
        if (!cancelled)
          console.error("Failed to initialize attendance:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    initialize();
    return () => {
      cancelled = true;
    };
  }, [props.classRosterId]);

  const sorting = (col) => {
    const sorted = [...attendanceData].sort((a, b) => {
      // ASTT-7319: was a[col]?.toLowerCase() -- optional chaining only
      // guards against null/undefined, not against calling a string
      // method on a genuinely non-null value. classRosterId/oracleId are
      // numeric; sorting by either of those columns would throw here
      // (numbers have no .toLowerCase()). String(...) first makes this
      // safe for both numeric and text columns.
      const valA = String(a[col] ?? "").toLowerCase();
      const valB = String(b[col] ?? "").toLowerCase();

      if (order === "ASC") {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });

    setAttendanceData(sorted);
    setOrder(order === "ASC" ? "DSC" : "ASC");
    setSortFlag({ [col]: order });
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Box sx={fontStyles}>
        <Grid container>
          <Grid item xs={12}>
            <TableContainer className={classes.container} component={Paper}>
              <Table id="dtrAttendanceTable" size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {dynamicColumns.length > 0
                      ? StaticColumns.map((col, index) => {
                          const leftOffset = StaticColumns.slice(
                            0,
                            index,
                          ).reduce((acc, curr) => acc + (curr.width || 150), 0);
                          return (
                            <TableCell
                              key={col.id}
                              className={`${classes.tableHeaderCell} text-center align-center`}
                              sx={{
                                position: "sticky",
                                left: leftOffset,
                                top: 0,
                                zIndex: 1100,
                                boxShadow:
                                  index === StaticColumns.length - 1
                                    ? "2px 0 5px -2px rgba(0,0,0,0.15)"
                                    : undefined,
                                cursor: "pointer",
                                backgroundColor: "#e5e4e2",
                                minWidth: col.width || 150,
                              }}
                              // ASTT-7319: a raw onClick on a TableCell is
                              // not keyboard-focusable and isn't announced
                              // as an interactive control by screen
                              // readers -- the same gap fixed earlier in
                              // RichTextEditorField.js. role/tabIndex/
                              // onKeyDown make this a real, operable
                              // sort control; aria-label states the
                              // action and current sort state together.
                              role="button"
                              tabIndex={0}
                              aria-label={`Sort by ${col.label}${
                                sortFlag[col.id]
                                  ? `, currently sorted ${
                                      sortFlag[col.id] === "ASC"
                                        ? "ascending"
                                        : "descending"
                                    }`
                                  : ""
                              }`}
                              onClick={() => sorting(col.id)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  sorting(col.id);
                                }
                              }}
                            >
                              {col.label}
                              <ArrowUpwardIcon
                                className={`${classes.arrowIcon} ${
                                  sortFlag[col.id] === "ASC"
                                    ? classes.arrowAsc
                                    : classes.arrowInactive
                                }`}
                              />
                              <ArrowDownwardIcon
                                className={`${classes.arrowIcon} ${
                                  sortFlag[col.id] === "DSC"
                                    ? classes.arrowDesc
                                    : classes.arrowInactive
                                }`}
                              />
                            </TableCell>
                          );
                        })
                      : null}

                    {/* Dynamic date column headers */}
                    {dynamicColumns.length > 0
                      ? dynamicColumns.map((col, id) => {
                          const date =
                            col instanceof Date ? col : new Date(col);
                          const localDate = new Date(
                            date.getUTCFullYear(),
                            date.getUTCMonth(),
                            date.getUTCDate() + 1,
                          );
                          return (
                            <TableCell
                              key={id}
                              align="center"
                              className={`${classes.tableHeaderCell} text-center align-center`}
                            >
                              {formatDate(localDate)}
                            </TableCell>
                          );
                        })
                      : null}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {attendanceData.map((row, rIdx) => (
                    <TableRow
                      key={`${row.classRosterId}-${row.oracleId}`}
                      hover
                      sx={{
                        "&:nth-of-type(even)": { backgroundColor: "#f5f7fa" },
                      }}
                    >
                      {/* Static cells */}
                      {StaticColumns.map((col, index) => {
                        // Calculate left offset for sticky positioning
                        const leftOffset = StaticColumns.slice(0, index).reduce(
                          (acc, curr) => acc + (curr.width || 150),
                          0,
                        );

                        return (
                          <TableCell
                            key={col.id}
                            sx={{
                              position: "sticky",
                              left: leftOffset,
                              zIndex: 100,
                              backgroundColor: "#fff",
                              fontSize: 12,
                              whiteSpace: "nowrap",
                              padding: "4px 12px",
                              borderRight: "1px solid #e0e0e0",
                              minWidth: col.width || 150,
                            }}
                          >
                            {row[col.id] ?? "—"}
                          </TableCell>
                        );
                      })}

                      {/* Dynamic date cells */}
                      {dynamicColumns.map((date, dateId) => {
                        const dateKey = toDateKey(date);
                        const hours = row.attendance?.[dateKey];
                        const phaseColor = getPhaseColor(row, dateKey);

                        return (
                          <Tooltip
                            title={phaseColor?.tooltip ?? ""}
                            arrow
                            disableHoverListener={!phaseColor}
                          >
                            <TableCell
                              key={dateId}
                              align="center"
                              sx={{
                                fontSize: 12,
                                padding: "4px 6px",
                                borderRight: "1px solid #e0e0e0",
                                backgroundColor: phaseColor
                                  ? phaseColor.bg
                                  : `${getWeekBg(date)}18`,
                                fontWeight: phaseColor ? 700 : 400,
                              }}
                            >
                              {hours ?? ""}
                            </TableCell>
                          </Tooltip>
                        );
                      })}
                    </TableRow>
                  ))}

                  {!loading && attendanceData.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={StaticColumns.length + dynamicColumns.length}
                        align="center"
                        sx={{ py: 4, color: "#9e9e9e", fontSize: 13 }}
                      >
                        No records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
Attendance.propTypes = {
  rosterId: PropTypes.string,
};
